import { useNavigate } from 'react-router-dom'
import { Button, ButtonGroup } from '@progress/kendo-react-buttons'
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import React, { Dispatch, SetStateAction } from 'react'
import { CommonActions, ConfirmReplyActions, StatusCheckActions, useAppDispatch, useAppSelector } from '../../../stores'
import { StatusCheckProps } from './StatusCheck'
import { forwardGuard } from '../../../components/hooks'
import { Authority } from '../../../gateway/system/Authority'
import './StatusCheck.css'
import { PutSupplierHearingStatusItemReqParams } from '@gateway.Supplier'
import { PutSupplierHearingStatusItemGateway } from '../../../gateway/supplier/PutSupplierHearingStatusItemGateway'
import { ErrorMessage } from '../../../components/parts'

export interface StatusCheckCardProps {
  propsInfo: StatusCheckProps
  statusCheckListIndex: number
  handleReplyReqFlgEvent: (value: number, index: number) => void
  handleHearingSheetFlgEvent: (value: number, index: number) => void
  handleOutputFlgEvent: (value: number, index: number) => void
  changeId: Dispatch<SetStateAction<string>>
  changeArgslist: Dispatch<SetStateAction<string[]>>
  changesDemandIndex: Dispatch<SetStateAction<number>>
  changeCancelIndex: Dispatch<SetStateAction<number>>
  changeMessageDialogshow: Dispatch<React.SetStateAction<boolean>>
  skip: number
  isChanged: boolean
  setErrorMessages: Dispatch<React.SetStateAction<ErrorMessage[]>>
}

export function StatusCheckCard(props: StatusCheckCardProps) {
  const navigate = useNavigate()
  const userProp = useAppSelector((state) => state.common.userProp)

  const target = useAppSelector(
    (state) => state.statuscheck.target
  )

  // シミュレーション年月度
  const simulatedYear = Number(
    props.propsInfo.simulationYm.slice(0, 4)
  )
  const simulatedMonth = Number(
    props.propsInfo.simulationYm.slice(4, 6)
  )
  // システム日付の年月
  const year = new Date().getFullYear()
  const month = new Date().getMonth() + 1
  const dispatch = useAppDispatch()

  /** M1回答依赖 */
  const handleReplyReqFlg = (event: SwitchChangeEvent) => {
    props.handleReplyReqFlgEvent(
      event.value ? 1 : 0,
      props.skip + props.statusCheckListIndex
    )
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
  }

  /**  M1ヒアリングシート */
  const handleHearingSheetFlg = (event: SwitchChangeEvent) => {
    props.handleHearingSheetFlgEvent(
      event.value ? 1 : 0,
      props.skip + props.statusCheckListIndex
    )
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
  }

  /** M1出力対象 */
  const handleOutputFlg = (event: SwitchChangeEvent) => {
    props.handleOutputFlgEvent(
      event.value ? 1 : 0,
      props.skip + props.statusCheckListIndex
    )
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
  }

  /** 督促 */
  const demandHandle = () => {
    props.changeId('I007')
    props.changeArgslist(['督促'])
    props.changeMessageDialogshow(true)
    props.changesDemandIndex(props.skip + props.statusCheckListIndex)
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
  }

  /** 取消 */
  const cancelHandle = () => {
    props.changeId('I007')
    props.changeArgslist(['取消'])
    props.changeMessageDialogshow(true)
    props.changeCancelIndex(props.skip + props.statusCheckListIndex)
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
  }

  /** 回答確認 */
  const handleConfirm = () => {
    dispatch(
      ConfirmReplyActions.setConfrimReplyInfo({
        simulationYm: props.propsInfo.simulationYm,
        branchCd: props.propsInfo.branchCd,
        branchNm: props.propsInfo.branchNm,
        supplierCd: props.propsInfo.supplierCd,
        supplierNm: props.propsInfo.supplierNm,
        supplierBranchCd: props.propsInfo.supplierBranchCd,
        supplierBranchNm: props.propsInfo.supplierBranchNm ?? '',
        factoryCd: props.propsInfo.factoryCd,
        factoryNm: props.propsInfo.factoryNm,
        expenseCd: props.propsInfo.expenseCd,
        expense: props.propsInfo.expense,
        hearingSheetFlg: props.propsInfo.hearingSheetFlg,
        replyYm: props.propsInfo.replyYm
      })
    )
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))
    forwardGuard('内容が編集中です。このページを離れますか？', navigate, '/supplier/confirm-reply', props.isChanged)
  }

  /** 事前調整 */
  const handlePreAdjustment = () => {
    dispatch(
      ConfirmReplyActions.setConfrimReplyInfo({
        simulationYm: props.propsInfo.simulationYm,
        branchCd: props.propsInfo.branchCd,
        branchNm: props.propsInfo.branchNm,
        supplierCd: props.propsInfo.supplierCd,
        supplierNm: props.propsInfo.supplierNm,
        supplierBranchCd: props.propsInfo.supplierBranchCd,
        supplierBranchNm: props.propsInfo.supplierBranchNm ?? '',
        factoryCd: props.propsInfo.factoryCd,
        factoryNm: props.propsInfo.factoryNm,
        expenseCd: props.propsInfo.expenseCd,
        expense: props.propsInfo.expense,
        hearingSheetFlg: props.propsInfo.hearingSheetFlg,
        replyYm: props.propsInfo.replyYm
      })
    )
    dispatch(StatusCheckActions.setTarget(`${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`))

    if (props.propsInfo.replySts === '未依頼') {
      const reqParamsSupplierHearingList: PutSupplierHearingStatusItemReqParams['data']['supplierHearingList'] = []
      const reqParamsItem = {
        simulationYm: '',
        expenseCd: '',
        supplierCd: '',
        supplierBranchCd: '',
        factoryCd: '',
        replyReqFlg: 0,
        hearingSheetFlg: 0,
        updDtm: null as string | null
      }
      reqParamsItem.simulationYm = props.propsInfo.simulationYm
      reqParamsItem.expenseCd = props.propsInfo.expenseCd
      reqParamsItem.supplierCd = props.propsInfo.supplierCd
      reqParamsItem.supplierBranchCd = props.propsInfo.supplierBranchCd
      reqParamsItem.factoryCd = props.propsInfo.factoryCd
      reqParamsItem.replyReqFlg = 0
      reqParamsItem.hearingSheetFlg = 0
      reqParamsItem.updDtm = props.propsInfo.updDtm
      reqParamsSupplierHearingList.push(reqParamsItem)

      const reqParams: PutSupplierHearingStatusItemReqParams = {
        data: {
          supplierHearingList: reqParamsSupplierHearingList,
          replyStsCdAfterChange: '8',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            navigate('/supplier/confirm-reply')
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              props.setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else {
      navigate('/supplier/confirm-reply')
    }
  }

  const statusColor =
    props.propsInfo.replySts === '督促中'
      ? '#D0611E'
      : props.propsInfo.replySts === '回答済'
        ? '#00A27C'
        : 'gray'

  return (
    <Card
      orientation="horizontal"
      className={
        target === `${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}` ? 'target-card-body' : ''
      }
    >
      <CardHeader>
        <CardTitle>
          <span
            style={{ fontSize: '1.5em', fontWeight: 'bold', color: 'gray' }}
          >
            {props.propsInfo.replyYm}
          </span>
        </CardTitle>
        <CardSubtitle>
          <span
            style={{ fontSize: '1.5em', fontWeight: 'bold', color: 'gray' }}
          >
            {props.propsInfo.expense}
          </span>
        </CardSubtitle>
        <CardSubtitle>
          <span
            style={{
              fontSize: '1.5em',
              fontWeight: 'bold',
              color: statusColor
            }}
          >
            {props.propsInfo.replySts}
          </span>
        </CardSubtitle>
      </CardHeader>
      <CardBody>
        <GridLayout
          cols={[{ width: 125 }, { width: 200 }, { width: 100 }]}
        >
          <GridLayoutItem col={1} row={1}>
            <span style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
              {props.propsInfo.branchCd === '01'
                ? props.propsInfo.branchNm
                : `${props.propsInfo.branchNm}支店`
              }
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={2} colSpan={3} row={1}>
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '1.5em'
              }}
            >
              {props.propsInfo.supplierNm}{' '}{props.propsInfo.supplierBranchNm}
              {props.propsInfo.factoryNm !== ''
                ? `${props.propsInfo.factoryNm}`
                : ''}
            </span>
          </GridLayoutItem>

          <GridLayoutItem col={1} row={2}>
            <span>回答期限：</span>
          </GridLayoutItem>
          <GridLayoutItem col={1} row={3}>
            <span>回答依頼日：</span>
          </GridLayoutItem>
          <GridLayoutItem col={1} row={4}>
            <span>回答日：</span>
          </GridLayoutItem>
          <GridLayoutItem col={1} row={5}>
            <span>確認日：</span>
          </GridLayoutItem>

          <GridLayoutItem col={2} row={2}>
            <span>TOP画面を参照してください</span>
          </GridLayoutItem>
          <GridLayoutItem col={2} row={3}>
            <span>
              {props.propsInfo.replyReqYmd !== null
                ? String(props.propsInfo.replyReqYmd).substring(0, 4) +
                  '/' +
                  String(props.propsInfo.replyReqYmd).substring(4, 6) +
                  '/' +
                  String(props.propsInfo.replyReqYmd).substring(6, 8)
                : ''}
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={2} row={4}>
            <span>
              {props.propsInfo.replyYmd !== null
                ? String(props.propsInfo.replyYmd).substring(0, 4) +
                  '/' +
                  String(props.propsInfo.replyYmd).substring(4, 6) +
                  '/' +
                  String(props.propsInfo.replyYmd).substring(6, 8)
                : ''}
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={2} row={5}>
            <span>
              {props.propsInfo.confirmationYmd !== null
                ? String(props.propsInfo.confirmationYmd).substring(0, 4) +
                  '/' +
                  String(props.propsInfo.confirmationYmd).substring(4, 6) +
                  '/' +
                  String(props.propsInfo.confirmationYmd).substring(6, 8)
                : ''}
            </span>
          </GridLayoutItem>

          <GridLayoutItem col={3} row={3}>
            <span>回答依頼者：</span>
          </GridLayoutItem>
          <GridLayoutItem col={3} row={4}>
            <span>回答者：</span>
          </GridLayoutItem>
          <GridLayoutItem col={3} row={5}>
            <span>確認者：</span>
          </GridLayoutItem>

          <GridLayoutItem col={4} row={3}>
            <span>{props.propsInfo.replyReqPerson}</span>
          </GridLayoutItem>
          <GridLayoutItem col={4} row={4}>
            <span>{props.propsInfo.replyPerson}</span>
          </GridLayoutItem>
          <GridLayoutItem col={4} row={5}>
            <span>{props.propsInfo.confirmationPerson}</span>
          </GridLayoutItem>
        </GridLayout>
      </CardBody>
      <CardFooter>
        <GridLayout
          cols={[{ width: 100 }, { width: 100 }, { width: 100 }]}
        >
          <GridLayoutItem row={1} col={1} colSpan={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonGroup>
              {userProp?.authority !== Authority.Supplier
                ? (
                <Button
                  themeColor="secondary"
                  fillMode="flat"
                  onClick={handlePreAdjustment}
                  disabled={
                    (props.propsInfo.replySts !== '未依頼' && props.propsInfo.replySts !== '事前調整') ||
                    simulatedYear < year ||
                    (simulatedYear === year && simulatedMonth < month)
                  }
                >
                  事前調整
                </Button>
                  )
                : <></>
              }
              <Button
                themeColor="secondary"
                fillMode="flat"
                onClick={handleConfirm}
                disabled={props.propsInfo.replySts === '未依頼' || props.propsInfo.replySts === '事前調整'}
                autoFocus={target === `${props.propsInfo.expenseCd}-${props.propsInfo.supplierCd}-${props.propsInfo.supplierBranchCd}-${props.propsInfo.factoryCd}`}
              >
                {props.propsInfo.replySts === '回答済' ||
                props.propsInfo.replySts === '確認済' ||
                props.propsInfo.replySts === '未依頼' ||
                props.propsInfo.replySts === '事前調整'
                  ? '回答確認'
                  : '回答入力'}
              </Button>
              <Button
                themeColor="secondary"
                fillMode="flat"
                onClick={demandHandle}
                disabled={
                  (userProp?.authority === Authority.Branch && userProp?.branchCode !== props.propsInfo.branchCd) ||
                  userProp?.authority === Authority.Supplier ||
                  props.propsInfo.replyStsCd === '1' ||
                  props.propsInfo.replyStsCd === '5' ||
                  props.propsInfo.replyStsCd === '7' ||
                  props.propsInfo.replyStsCd === '8' ||
                  simulatedYear < year ||
                  (simulatedYear === year && simulatedMonth < month)
                }
              >
                督促
              </Button>
              <Button
                themeColor="secondary"
                fillMode="flat"
                onClick={cancelHandle}
                disabled={
                  (userProp?.authority === Authority.Branch && userProp?.branchCode !== props.propsInfo.branchCd) ||
                  userProp?.authority === Authority.Supplier ||
                  props.propsInfo.replyStsCd === '1' ||
                  props.propsInfo.replyStsCd === '7' ||
                  simulatedYear < year ||
                  (simulatedYear === year && simulatedMonth < month)
                }
              >
                取消
              </Button>
            </ButtonGroup>
          </GridLayoutItem>

          <GridLayoutItem row={2} col={2} colSpan={2}>
            <GridLayout gap={{ rows: 2, cols: 5 }} style={{ marginTop: '0.8em' }}>
              <GridLayoutItem col={1} row={1}>
                <span style={{ fontSize: '0.8em' }}>回答依頼</span>
              </GridLayoutItem>
              <GridLayoutItem col={2} row={1}>
                <Switch
                  size={'small'}
                  disabled={
                    (userProp?.authority === Authority.Branch && userProp?.branchCode !== props.propsInfo.branchCd) ||
                    (userProp?.authority === Authority.Supplier) ||
                    (props.propsInfo.replyStsCd !== '1' && props.propsInfo.replyStsCd !== '8') ||
                    simulatedYear < year ||
                    (simulatedYear === year && simulatedMonth < month)
                  }
                  checked={props.propsInfo.replyReqFlg === 1}
                  onChange={handleReplyReqFlg}
                />
              </GridLayoutItem>
              <GridLayoutItem col={1} row={2}>
                <span style={{ fontSize: '0.8em' }}>ヒアリングシート</span>
              </GridLayoutItem>
              <GridLayoutItem col={2} row={2}>
                <Switch
                  size={'small'}
                  disabled={
                    (userProp?.authority === Authority.Branch && userProp?.branchCode !== props.propsInfo.branchCd) ||
                    (userProp?.authority === Authority.Supplier) ||
                    (props.propsInfo.replyStsCd !== '1' && props.propsInfo.replyStsCd !== '8') ||
                    props.propsInfo.hearingSheetTargetFlg !== 1 ||
                    simulatedYear < year ||
                    (simulatedYear === year && simulatedMonth < month)
                  }
                  checked={props.propsInfo.hearingSheetFlg === 1}
                  onChange={handleHearingSheetFlg}
                />
              </GridLayoutItem>
              <GridLayoutItem col={1} row={3}>
                <span style={{ fontSize: '0.8em' }}>出力対象</span>
              </GridLayoutItem>
              <GridLayoutItem col={2} row={3}>
                <Switch
                  size={'small'}
                  checked={props.propsInfo.outPutFlg === 1}
                  disabled={props.propsInfo.replyStsCd !== '7'}
                  onChange={handleOutputFlg}
                />
              </GridLayoutItem>
            </GridLayout>
          </GridLayoutItem>
        </GridLayout>
      </CardFooter>
    </Card>
  )
}
